import CryptoJS from 'crypto-js'
import moment from 'moment-timezone'

const STORAGE_KEY = 'TMT#publisher#auth'
const SECRET = process.env.VUE_APP_AUTH_SECRET

if (!SECRET) {
  console.error('[authService] VUE_APP_AUTH_SECRET não está definida.')
}

// ── Criptografia ──────────────────────────────────────────────────────────────

function encryptPayload(payload) {
  return CryptoJS.AES.encrypt(JSON.stringify(payload), SECRET).toString()
}

function decryptPayload(cipher) {
  try {
    const bytes = CryptoJS.AES.decrypt(cipher, SECRET)
    const raw = bytes.toString(CryptoJS.enc.Utf8)
    if (!raw) return null
    return JSON.parse(raw)
  } catch {
    return null
  }
}

// ── Leitura ───────────────────────────────────────────────────────────────────
// keepConnected=true  → localStorage  (sobrevive ao fechar o browser)
// keepConnected=false → sessionStorage (morre ao fechar o browser)

function getRawCipher() {
  return localStorage.getItem(STORAGE_KEY) || sessionStorage.getItem(STORAGE_KEY)
}

export function getAuth() {
  try {
    const cipher = getRawCipher()
    if (!cipher) return null
    const data = decryptPayload(cipher)
    if (!data) {
      clearAuth()
      return null
    }
    return data
  } catch {
    clearAuth()
    return null
  }
}

export function getAuthToken() {
  const data = getAuth()
  if (!data?.security) return null
  return {
    accessKey: data.security.accessKey,
    secureKey:  data.security.secureKey,
  }
}

// ── Gravação após login/confirmação ──────────────────────────────────────────

export function saveAuth(responseData, { keepConnected = false } = {}) {
  const { options, plan, profileAccessId, profileTypeId, security, user, whitelabel } = responseData

  if (user.timezone === '-3' || user.timezone === -3) {
    user.timezone = 'America/Sao_Paulo'
  } else if (typeof user.timezone !== 'string' || !moment.tz.zone(user.timezone)) {
    user.timezone = 'America/Sao_Paulo'
  }

  const payload = {
    // campos idênticos ao antigo 'auth'
    log:           [],
    profile:       options.profiles[options.profileId],
    profileId:     options.profileId,
    profilePhones: options.profilePhones,
    profiles:      options.profiles,
    lang:          options.lang,
    plan,
    type: { access: profileAccessId, id: profileTypeId },
    user,
    whitelabel,
    // tokens (antigo 'authToken')
    security: { accessKey: security.accessKey, secureKey: security.secureKey },
    savedAt: new Date().toISOString(),
  }

  const cipher = encryptPayload(payload)

  if (keepConnected) {
    localStorage.setItem(STORAGE_KEY, cipher)
    sessionStorage.removeItem(STORAGE_KEY)
  } else {
    sessionStorage.setItem(STORAGE_KEY, cipher)
    localStorage.removeItem(STORAGE_KEY)
  }

  moment.tz.setDefault(user.timezone)
  return payload
}

// ── Remoção (logout) ──────────────────────────────────────────────────────────

export function clearAuth() {
  localStorage.removeItem(STORAGE_KEY)
  sessionStorage.removeItem(STORAGE_KEY)
  // limpa chaves do formato antigo
  localStorage.removeItem('auth')
  localStorage.removeItem('authToken')
}

// ── Logout com chamada ao backend ─────────────────────────────────────────────
// axiosInstance é opcional — o componente passa this.$axios

export async function setLogout(axiosInstance = null) {
  clearAuth()
  if (axiosInstance) {
    try {
      await axiosInstance.post('auth/logout')
    } catch { /* ignora erros de rede */ }
  }
}

// ── Redirect pós-login ────────────────────────────────────────────────────────

export function getRedirectPath(auth, queryR) {
  if (queryR) return decodeURIComponent(queryR)
  return auth?.whitelabel?.w?.enabled ? '/dashboard' : '/app/home'
}

export function saveAuthAndRedirect(responseData, queryR, { keepConnected = false } = {}) {
  const auth = saveAuth(responseData, { keepConnected })
  const path = getRedirectPath(auth, queryR)
  // verifica gravação antes de avançar
  if (!getRawCipher()) {
    console.error('[authService] Auth não gravado — abortando redirect')
    return
  }
  setTimeout(() => { window.location.href = path }, 80)
}
